import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import lockDeskApi from '../../services/lockDeskApi';
import marketTrendApi from '../../services/marketTrendApi';
import { piper } from '../../theme/theme';

/** Slot id for pages to portal their per-page header content into the header bar. */
export const PAGE_HEADER_SLOT_ID = 'app-page-header-slot';

const trendArrow = (trend) => (trend === 'UP' ? '▲' : trend === 'DOWN' ? '▼' : '·');

/** Market context; lock-desk indicator only when lender admin has closed it. */
const HeaderStatus = ({ onOpenAdvisor }) => {
  const { data: lockDesk } = lockDeskApi.endpoints.getLockDeskStatus.useQuery();
  const { data: market } = marketTrendApi.endpoints.getMarketTrends.useQuery(
    { loanCloseDays: 30 },
    { pollingInterval: 5 * 60 * 1000 },
  );

  const tenYr = market?.analysis?.treasury?.latest;
  const trend = market?.recommendation?.treasuryTrend;
  const deskClosed = lockDesk != null && lockDesk.isEnabled === false;

  const parts = [];
  if (deskClosed) parts.push('🔒 Lock desk closed');
  if (tenYr != null) parts.push(`10yr ${Number(tenYr).toFixed(2)}% ${trendArrow(trend)}`);
  if (parts.length === 0) return null;

  return (
    <Typography
      component={onOpenAdvisor ? 'button' : 'div'}
      type={onOpenAdvisor ? 'button' : undefined}
      onClick={onOpenAdvisor}
      sx={{
        fontSize: '11.5px',
        fontWeight: 600,
        color: deskClosed ? piper.redDark : piper.inkSecondary,
        whiteSpace: 'nowrap',
        background: 'none',
        border: 'none',
        fontFamily: piper.font,
        cursor: onOpenAdvisor ? 'pointer' : 'default',
        '&:hover': onOpenAdvisor ? { color: piper.ink } : undefined,
      }}
    >
      {parts.join(' · ')}
    </Typography>
  );
};

/**
 * PiperPageHeader — white page header bar: PRO Portal logo, hairline divider,
 * page title (or per-page portal content), contextual status, and the
 * solid-red "+ New Loan" primary CTA.
 */
const PiperPageHeader = ({ brandLink = '/', title, showNewLoan, onNewLoan, onOpenAdvisor }) => {
  const location = useLocation();

  return (
    <Box
      component="header"
      sx={{
        backgroundColor: piper.surface,
        borderBottom: `1px solid ${piper.hairlineStrong}`,
        px: 3,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minHeight: 54,
      }}
    >
      <Box component={RouterLink} to={brandLink} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <Box component="img" src="/assets/pp-logo.png" alt="PRO Portal" sx={{ height: 26 }} />
      </Box>
      <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: piper.hairlineStrong, flexShrink: 0 }} />

      {/* Default page title — hidden when a page portals its own header content */}
      {title && (
        <Typography key={location.pathname} sx={{ fontSize: '15px', fontWeight: 700, color: piper.ink, whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
      )}

      {/* Per-page portal slot (Loan Detail fills this) */}
      <Box id={PAGE_HEADER_SLOT_ID} sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }} />

      <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
        <HeaderStatus onOpenAdvisor={onOpenAdvisor} />
        {showNewLoan && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onNewLoan}>
            New Loan
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default PiperPageHeader;
